import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import ParticleService from '../../ParticleService';
import { AgGridReact } from 'ag-grid-react';
import { RiWifiOffFill, RiWifiFill } from 'react-icons/ri';
import { BsFillPencilFill } from 'react-icons/bs';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import DeviceNote from '.././DeviceNote'
import FullWidthCellRenderer from "./FullWidthCellRenderer";
import Moment from 'react-moment';
import AddDevice from '../Modals/AddDevice';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '.././styles.scss';

async function fireAll(devices) {
  toast('Fire All')
  console.log("Fire All");
  devices.filter(obj => !obj.accessRestricted).forEach(obj => {
    ParticleService.fireCannon(obj.id).then(callback => {
      if (callback.error) {
        console.error(callback);
        toast.error("Could not fire " + obj.name)
      }
      return callback;
    });
  })
}

export default function DeviceTable({ isAdmin = false }) {
  const [rowData, setRowData] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const narrowView = window.innerWidth < 500 ? true : false;
  const [newGroupName, setnewGroupName] = useState('');
  const gridRef = useRef();

  useEffect(() => {
    fetchTableData()
    document.addEventListener('dataRefresh', (event) => {
      fetchTableData();
    });
  }, []);

  async function fetchTableData() {
    const [data, subscriptionSummary] = isAdmin
      ? [await ParticleService.getAdminFleetDevices(), null]
      : await Promise.all([ParticleService.getDevices(), ParticleService.getMySubscriptionStatus()]);
    const accessByDeviceId = new Map((subscriptionSummary?.devices || []).map(device => [device.particleDeviceId, device]));
    if (data.length > 0) {
      // Create rows
      let rowList = [];
      data.forEach(obj => {
        const access = accessByDeviceId.get(obj.id);
        let row = Object.assign({}, obj, {
          adminFleet: isAdmin,
          accessRestricted: access?.status === 'Restricted',
          accessStatus: access?.status,
        });
        row = Object.assign(row, { isFullWidthDetail: false }); // Add detail flag for full width printing
        rowList.push(row);      //Push to dataset
      });
      setAllRows(rowList);
      setRowData(rowList);
    } else {
      toast.error("No Data");
    }
    gridRef.current?.api?.sizeColumnsToFit()
  }

  useEffect(() => {
    if (!isAdmin) return;
    setRowData(allRows.filter(device =>
      (!customerFilter || device.customerEmail === customerFilter) &&
      (!productFilter || String(device.product_id) === productFilter) &&
      (!assignmentFilter || (assignmentFilter === 'assigned' ? device.assigned : !device.assigned)) &&
      (!onlineFilter || (onlineFilter === 'online' ? device.online : !device.online)) &&
      (!subscriptionFilter || device.subscriptionStatus === subscriptionFilter)));
  }, [allRows, assignmentFilter, customerFilter, isAdmin, onlineFilter, productFilter, subscriptionFilter]);

  const customers = useMemo(() => [...new Set(allRows.map(device => device.customerEmail).filter(Boolean))].sort(), [allRows]);
  const subscriptionStatuses = useMemo(() => [...new Set(allRows.map(device => device.subscriptionStatus).filter(Boolean))].sort(), [allRows]);

  const rowClick = useCallback((params) => {
    //Rework data
    const clickedRow = params.data;
    if (clickedRow.isFullWidthDetail || params.column.colId === 'name')
      return;
    console.log(params.columnApi)
    let currentFullWidth = '';
    let newArray = [];
    //Find current full width
    gridRef.current.api.forEachNode(function (node) {
      if (node.data.isFullWidthDetail) {
        currentFullWidth = node.data.id;
      }
    });
    //Re-add existing data
    gridRef.current.api.forEachNode(function (node) {
      const nodeID = node.data.id;
      //Add none detail rows
      if (!node.data.isFullWidthDetail) {
        newArray.push(node.data);
      }

      //Insert new detail row from clicked row
      if ((nodeID === clickedRow.id) && (!node.data.isFullWidthDetail) && (clickedRow.id !== currentFullWidth)) {
        let newRow = Object.assign({}, node.data);
        newRow.isFullWidthDetail = true;
        newArray.push(newRow);
      }
    });
    setRowData(newArray);
    gridRef.current.api.sizeColumnsToFit();
  }, []);


  const isFullWidthRow = useCallback((params) => {
    return params.rowNode.data.isFullWidthDetail;
  }, []);

  const fullWidthCellRenderer = useMemo(() => {
    return FullWidthCellRenderer;
  }, []);

  const getRowHeight = useCallback((params) => {
    // return larger height for full width rows
    if (params.data.isFullWidthDetail) {
      if (params.data.accessRestricted)
        return 190;
      if (narrowView)
        return 700;
      else
        return 310;
    } else {
      return 46;
    }
  }, []);

  function onlineIcon(props) {
    const dateToFormat = props.data.last_heard;
    if (props.value === false) {
      return <div><RiWifiOffFill size="20px" color="red" />&nbsp;{narrowView ? null : <Moment date={dateToFormat} format="DD-MMM-YY" />}</div>
    } else {
      return <div><RiWifiFill size="20px" color="green" /></div>
    }
  }

  function setGroupName(e) {
    const re = /^[a-zA-Z0-9-_ \b]+$/;
    if (e.target.value === '' || re.test(e.target.value))
      setnewGroupName(e.target.value)
  }

  function groupDevices() {
    if (!newGroupName) {
      toast.error("Enter a Group name")
      return;
    }
    toast('Grouping Devices');
    const rowstoGroup = gridRef.current.api.getSelectedRows();
    rowstoGroup.forEach(node => {
      if (!node.isFullWidthDetail) {
        if (!node.accessRestricted)
          ParticleService.setDeviceNotes(node.id, DeviceNote.addUserGroup(node.notes, newGroupName), isAdmin ? node.product_id : null);
      }
    })
    document.dispatchEvent(new Event('dataRefresh'));
    setnewGroupName('');
  }

  function ActionButtons(props) {
    if (props.data.accessRestricted)
      return <span className="badge bg-danger">Renew access</span>;
    return <div><button className="btn btn-danger" ref={(ref) => {
      if (!ref) return;
      //attach onClick event handler to the real DOM element
      ref.onclick = (e) => {
        e.stopPropagation();
        if (isAdmin && !window.confirm(`Fire ${props.data.name || props.data.id} for ${props.data.customerEmail || 'an unassigned device'}?`)) return;
        toast("FIRING! " + props.data.name)
        ParticleService.fireCannon(props.data.id, isAdmin ? props.data.product_id : null).then((result) => {
          if (result.error)
            toast.error(props.data.name + " " + result.error)
        });
      };
    }}>
      Fire
    </button >{' '}
      <button className="btn btn-secondary">
        Details
      </button >
    </div >
  }

  function nameFieldRender(props) {
    return <div >
      {props.value}{' '}
      {!props.data.accessRestricted && <BsFillPencilFill />}
    </div >
  }

  const [columnDefs] = useState(() => [
    {
      field: 'name', flex: '2', headerCheckboxSelection: true, checkboxSelection: params => !params.data.accessRestricted, cellRenderer: nameFieldRender, editable: params => !params.data.accessRestricted, valueSetter: params => {
        if (ParticleService.checkName(params.newValue)) {
          if (isAdmin) ParticleService.renameFleetDevice(params.data.id, params.data.product_id, params.newValue);
          else ParticleService.renameDevice(params.data.id, params.newValue, ParticleService.getUserToken());
          params.data.name = params.newValue;
        } else {
          toast.error("Invalid Device Name");
        }
        return true;
      }
    },
    { field: 'id', flex: '2', hide: narrowView },
    ...(isAdmin ? [
      { field: 'customerEmail', headerName: 'Customer', flex: '2' },
      { field: 'productName', headerName: 'Product', flex: '1' },
      { field: 'assigned', headerName: 'Assignment', flex: '1', valueFormatter: params => params.value ? 'Assigned' : 'Unassigned' },
      { field: 'subscriptionStatus', headerName: 'Subscription', flex: '1' },
    ] : []),
    { field: 'action', minWidth: 150, flex: '1', cellRenderer: ActionButtons, },
    { field: 'online', flex: '1', cellRenderer: onlineIcon, }
  ])

  const gridOptions = {
    columnDefs: columnDefs,
    defaultColDef: {
      sortable: true,
      resizable: true,
    },
    suppressHorizontalScroll: !isAdmin,
    rowSelection: 'multiple',
    onCellClicked: rowClick,
    suppressRowClickSelection: true,
    isRowSelectable: params => isAdmin || !params.data.accessRestricted,
    isFullWidthRow: isFullWidthRow,
    fullWidthCellRenderer: fullWidthCellRenderer,
    getRowHeight: getRowHeight,
    enableCellTextSelection: true,
    statusBar: {
      statusPanels: [{ statusPanel: 'agAggregationComponent' }],
    },
  };

  return (
    <div>
      <div className="card-header">
        {isAdmin && <div className="row g-2 mb-3">
          <div className="col-md"><Form.Select aria-label="Filter by customer" value={customerFilter} onChange={event => setCustomerFilter(event.target.value)}><option value="">All customers</option>{customers.map(customer => <option key={customer} value={customer}>{customer}</option>)}</Form.Select></div>
          <div className="col-md"><Form.Select aria-label="Filter by product" value={productFilter} onChange={event => setProductFilter(event.target.value)}><option value="">All products</option><option value={ParticleService.LTEProductID}>LTE</option><option value={ParticleService.WIFIProductID}>Wi-Fi</option></Form.Select></div>
          <div className="col-md"><Form.Select aria-label="Filter by assignment" value={assignmentFilter} onChange={event => setAssignmentFilter(event.target.value)}><option value="">All assignments</option><option value="assigned">Assigned</option><option value="unassigned">Unassigned</option></Form.Select></div>
          <div className="col-md"><Form.Select aria-label="Filter by online status" value={onlineFilter} onChange={event => setOnlineFilter(event.target.value)}><option value="">Online and offline</option><option value="online">Online</option><option value="offline">Offline</option></Form.Select></div>
          <div className="col-md"><Form.Select aria-label="Filter by subscription" value={subscriptionFilter} onChange={event => setSubscriptionFilter(event.target.value)}><option value="">All subscriptions</option>{subscriptionStatuses.map(status => <option key={status} value={status}>{status}</option>)}</Form.Select></div>
        </div>}
        <div className='deviceButtonHeader'>
          <div className='textWindow'>
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 500 }}
              overlay={<Tooltip id="button-tooltip" >
                Name may only contain letters, numbers, spaces, dashes, and underscores
              </Tooltip>}>
              <FloatingLabel controlId="floatingInput" label="Group Name">
                <Form.Control type="text" onChange={setGroupName} value={newGroupName} />
              </FloatingLabel>
            </OverlayTrigger>
          </div>
          <div className='flexitem'>
            <Button variant="primary" onClick={groupDevices}>Group Devices</Button>
          </div>
          {!isAdmin && <div className='flexitem'>
            <AddDevice />
          </div>}
          {!isAdmin && allRows.some(device => !device.accessRestricted) && <div className='flexitem firebutton'>
            <Button variant="danger" onClick={() => fireAll(allRows)}>Fire ALL</Button>
          </div>}
        </div>
      </div>
      <div className="ag-theme-alpine table-container" >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          gridOptions={gridOptions}>
        </AgGridReact>
      </div >
    </div >
  );
}
